import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Eye, 
  Mail, 
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Search,
  RefreshCw,
  Trash2
} from 'lucide-react';
import { useAdmin } from '@hooks/useAdmin';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS } from '@constants/admin.constants';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import StatsCard from '@components/admin/StatsCard';
import FilterBar from '@components/admin/FilterBar';
import Pagination from '@components/admin/Pagination';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const MentorList = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { getDashboardStats, getMentorList, deleteMentor, loading } = useAdmin();
  
  const [stats, setStats] = useState(null);
  const [mentors, setMentors] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    page: 1,
    limit: 10
  });

  const statusFilters = [
    { value: 'all', label: 'All Mentors' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_MENTORS)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [statsRes, mentorsRes] = await Promise.all([
        getDashboardStats(),
        getMentorList(filters)
      ]);

      if (statsRes.success) {
        setStats(statsRes.data);
      }

      if (mentorsRes.success) {
        setMentors(Array.isArray(mentorsRes.data) ? mentorsRes.data : []);
        setPagination(mentorsRes.pagination || {});
      }
    } catch (error) {
      toast.error('Failed to load mentor data');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleViewDetails = (mentorId) => {
    navigate(`/admin/mentors/${mentorId}`);
  };

  const handleDeleteMentor = async (mentorId, mentorName) => {
    if (!window.confirm(`Are you sure you want to delete ${mentorName}? This action will block the mentor from logging in and remove them from all listings.`)) {
      return;
    }

    try {
      const response = await deleteMentor(mentorId);
      if (response.success) {
        toast.success('Mentor deleted successfully');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete mentor');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getDocumentStats = (documents) => {
    const total = documents?.length || 0;
    const approved = documents?.filter(d => d.status === 'approved').length || 0;
    const pending = documents?.filter(d => d.status === 'pending').length || 0;
    const rejected = documents?.filter(d => d.status === 'rejected').length || 0;
    const updated = documents?.filter(d => d.isUpdated === true).length || 0;
    return { total, approved, pending, rejected, updated };
  };

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_MENTORS}>
      <div className="min-h-full bg-gray-50 p-3">
        {/* <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            Mentor Management
          </h1>
          <p className="text-primary-600">
            Manage and verify mentor applications
          </p>
        </div> */}

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <StatsCard
              title="Total Mentors"
              value={stats.totalMentors || 0}
              icon={Users}
              color="primary"
            />
            <StatsCard
              title="Pending Review"
              value={stats.pendingMentors || 0}
              icon={Clock}
              color="yellow"
            />
            <StatsCard
              title="Approved"
              value={stats.approvedMentors || 0}
              icon={CheckCircle}
              color="green"
            />
            <StatsCard
              title="Pending Documents"
              value={stats.pendingDocuments || 0}
              icon={FileText}
              color="blue"
            />
          </div>
        )}

        <Card className="mb-3 p-4">
          <FilterBar
            searchValue={filters.search}
            onSearchChange={(value) => handleFilterChange('search', value)}
            filters={statusFilters}
            activeFilter={filters.status}
            onFilterChange={(value) => handleFilterChange('status', value)}
            placeholder="Search by name, email, or phone..."
          />
        </Card>

        <Card className="overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <h2 className="text-base font-semibold text-primary-900">
              All Mentors
            </h2>
          </div>

          {loading ? (
            <div className="p-8 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : mentors.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-16 h-16 text-primary-300 mx-auto mb-4" />
              <p className="text-primary-600">No mentors found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mentor
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documents
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Registered
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {mentors.map((mentor) => {
                      const docStats = getDocumentStats(mentor.documents);
                      return (
                        <tr key={mentor.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              {mentor.profilePicture ? (
                                <img
                                  src={`${import.meta.env.VITE_API_BASE_URL.replace('/api', '')}${mentor.profilePicture}`}
                                  alt={`${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.email}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-semibold">
                                  {mentor.firstName?.[0]?.toUpperCase() || mentor.lastName?.[0]?.toUpperCase() || mentor.email?.[0]?.toUpperCase()}
                                </div>
                              )}
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {`${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 text-sm text-gray-900">
                                <Mail className="w-4 h-4 text-gray-500" />
                                {mentor.email}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Phone className="w-4 h-4 text-gray-500" />
                                {mentor.phone || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex gap-2 text-xs flex-wrap">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-semibold">
                                ✓ {docStats.approved}
                              </span>
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded font-semibold">
                                ⏳ {docStats.pending}
                              </span>
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">
                                ✗ {docStats.rejected}
                              </span>
                              {docStats.updated > 0 && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-semibold flex items-center gap-1">
                                  <RefreshCw className="w-3 h-3" />
                                  {docStats.updated}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {getStatusBadge(mentor.adminApproved)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {new Date(mentor.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewDetails(mentor.id)}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Details
                              </Button>
                              {hasPermission(ADMIN_PERMISSIONS.DELETE_MENTORS) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteMentor(mentor.id, `${mentor.firstName || ''} ${mentor.lastName || ''}`.trim() || mentor.email)}
                                  className="text-red-600 hover:text-red-700 hover:border-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pagination && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </Card>
      </div>
    </PermissionGuard>
  );
};

export default MentorList;
